import React, { createContext, useContext } from "react";

import NavBar from "./navbar/NavBar";
import SideBar from "./sidebar/SideBar";
import { useTitle } from "./titleContext";
import { StaticProvider } from "../context/StaticContext";
import { NavigationProvider } from "../context/NavigationContext";
import { CurrencyProvider } from "../context/CurrencyContext";
import { GenerateContentProvider } from "../context/GenerateContentContext";

type Props = {
  children?: React.ReactNode;
  component?: React.ReactNode;
  title?: string;
};

const InsideScreenContext = createContext(false);

/**
 * Pass-through Screen. Pages nested inside the app layout render their
 * content straight through, and any `title` prop is pushed up to the
 * shared TitleContext so the real NavBar (rendered by DashboardLayout)
 * displays dynamic per-page titles. Route-metadata titles cover the
 * static cases; this handles the legacy `<Screen title={fn()}/>` sites.
 */
const NestedScreen: React.FC<Props> = ({ children, component, title }) => {
  useTitle(title ?? "");
  return <>{component ?? children}</>;
};

const LayoutScreen: React.FC<Props> = ({ children, component, title }) => (
  <InsideScreenContext.Provider value={true}>
    <div className="relative flex h-screen max-h-full w-screen max-w-full flex-row justify-start overflow-hidden bg-gray-50">
      <CurrencyProvider>
        <NavigationProvider>
          <GenerateContentProvider>
            <SideBar />
            <div className="z-[999999] flex w-full flex-col shadow-xl">
              <NavBar title={title} />
              <StaticProvider>{component ?? children}</StaticProvider>
            </div>
          </GenerateContentProvider>
        </NavigationProvider>
      </CurrencyProvider>
    </div>
  </InsideScreenContext.Provider>
);

const Screen: React.FC<Props> = (props) => {
  const nested = useContext(InsideScreenContext);
  return nested ? <NestedScreen {...props} /> : <LayoutScreen {...props} />;
};

export default Screen;
